import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { 
  LogIn, Loader, Heart, ChevronLeft, ChevronRight, Filter, 
  Search, X, Bed, Bath, Home, MapPin, Square, ArrowDownUp,
  CheckCircle, Building, Calendar, Castle, DollarSign
} from 'lucide-react';
import PropertyCard from '../components/PropertyCard';
import PropertyCardPro from './profiles/PropertyCardPro';
import { Card } from '../components/ui/card';
import { useToast } from '../components/ui/toast/use-toast';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { 
      duration: 0.6, 
      ease: "easeOut" 
    } 
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const cardAnimation = {
  hidden: { opacity: 0, y: 30, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { 
      type: "spring", 
      damping: 15,
      duration: 0.6
    }
  },
  hover: {
    y: -8,
    scale: 1.03,
    boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
    transition: { duration: 0.2 }
  }
};

// Componente de pestañas moderno para filtrado por tipo con glassmorphism
const TypeTabs = ({ activeType, onChange, counts }) => {
  const tabs = [
    { id: 'all', label: 'Todos', icon: Heart, color: 'indigo' },
    { id: 'propiedad', label: 'Propiedades', icon: Home, color: 'purple' },
    { id: 'alquiler', label: 'Alquileres', icon: Building, color: 'blue' },
    { id: 'proyecto', label: 'Proyectos', icon: Castle, color: 'orange' }
  ];

  const colorSchemes = {
    indigo: { gradient: "from-indigo-500 to-indigo-600" },
    purple: { gradient: "from-purple-500 to-purple-600" },
    blue: { gradient: "from-blue-500 to-blue-600" },
    orange: { gradient: "from-orange-500 to-orange-600" }
  };

  return (
    <div className="flex flex-wrap gap-3 mb-8">
      {tabs.map(tab => {
        const isActive = activeType === tab.id;
        const count = tab.id === 'all' 
          ? counts.total 
          : counts[tab.id] || 0;
        const colors = colorSchemes[tab.color];
          
        return (
          <motion.button
            key={tab.id}
            onClick={() => onChange(tab.id)}
            className={`relative flex items-center gap-2 px-5 py-3 rounded-xl transition-all duration-300 overflow-hidden
                      ${isActive 
                        ? `bg-gradient-to-r ${colors.gradient} text-white shadow-xl border-0` 
                        : 'backdrop-blur-xl bg-white/10 dark:bg-white/5 border border-white/20 dark:border-white/10 text-gray-700 dark:text-gray-200 hover:bg-white/20 dark:hover:bg-white/10 shadow-lg dark:shadow-black/30'
                      }`}
            whileHover={{ scale: 1.03, y: -2 }}
            whileTap={{ scale: 0.97 }}
          >
            {/* Gradient overlay para estado activo */}
            {isActive && (
              <div className={`absolute inset-0 bg-gradient-to-r ${colors.gradient} opacity-10`} />
            )}
            
            <tab.icon className={`w-5 h-5 z-10 ${
              isActive 
                ? 'text-white' 
                : `text-${tab.color}-500 dark:text-${tab.color}-400`
            }`} />
            <span className="font-medium z-10">{tab.label}</span>
            <span className={`ml-1 px-2 py-0.5 text-xs rounded-full z-10 backdrop-blur-sm ${
              isActive 
                ? 'bg-white/20 text-white border border-white/30' 
                : `bg-${tab.color}-500/20 text-${tab.color}-700 dark:text-${tab.color}-300 border border-${tab.color}-300/30 dark:border-${tab.color}-500/30`
            }`}>
              {count}
            </span>
          </motion.button>
        );
      })}
    </div>
  );
};

const PageButton = ({ onClick, disabled, children, direction }) => (
  <motion.button
    onClick={onClick}
    disabled={disabled}
    className={`group flex items-center gap-2 px-6 py-3 rounded-xl 
              transition-all duration-300
              ${disabled 
      ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
      : 'bg-gradient-to-r from-indigo-600 to-blue-600 text-white shadow-lg hover:shadow-xl dark:shadow-xl dark:hover:shadow-2xl'
    }`}
    whileHover={!disabled ? { scale: 1.05 } : {}}
    whileTap={!disabled ? { scale: 0.95 } : {}}
  >
    {direction === 'prev' && (
      <ChevronLeft className="w-5 h-5 transition-transform duration-300 group-hover:-translate-x-1" />
    )}
    <span className="font-medium">{children}</span>
    {direction === 'next' && (
      <ChevronRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
    )}
  </motion.button>
);

const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800">
    <motion.div
      className="flex flex-col items-center gap-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <motion.div
        className="w-16 h-16 border-4 border-indigo-200 dark:border-indigo-800 border-t-indigo-600 dark:border-t-indigo-400 rounded-full"
        animate={{ rotate: 360 }}
        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
      />
      <p className="text-indigo-700 dark:text-indigo-300 font-medium">Cargando tus favoritos...</p>
    </motion.div>
  </div>
);

const LoginPrompt = ({ onLogin }) => (
  <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-white dark:from-gray-900 dark:to-gray-800 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
    <motion.div 
      className="max-w-md w-full"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl dark:shadow-2xl p-8 text-center border border-gray-100 dark:border-gray-700">
        <motion.div
          className="bg-indigo-50 dark:bg-indigo-900/30 w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6"
          whileHover={{ rotate: 5, scale: 1.05 }}
          transition={{ type: "spring", stiffness: 400, damping: 10 }}
        >
          <Heart className="w-12 h-12 text-indigo-500 dark:text-indigo-400" />
        </motion.div>
        
        <motion.h2 
          className="text-3xl font-bold mb-4 text-center text-gray-800 dark:text-gray-100"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.6 }}
        >
          Descubre tus favoritos
        </motion.h2>
        
        <motion.p 
          className="text-gray-600 dark:text-gray-400 mb-8 text-center leading-relaxed"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.6 }}
        >
          Inicia sesión para acceder a tus propiedades guardadas y gestionar tu colección personalizada.
        </motion.p>
        
        <motion.button
          onClick={onLogin}
          className="w-full flex items-center justify-center gap-2 px-6 py-4 rounded-xl
                    bg-gradient-to-r from-indigo-600 to-blue-600 
                    text-white font-medium transition-all duration-300 shadow-lg hover:shadow-xl dark:shadow-xl dark:hover:shadow-2xl"
          whileHover={{ scale: 1.03, boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)" }}
          whileTap={{ scale: 0.97 }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.6 }}
        >
          <LogIn className="w-5 h-5" />
          Iniciar Sesión
        </motion.button>
      </div>
    </motion.div>
  </div>
);

const EmptyState = ({ activeType }) => {
  let message = '';
  let icon = Heart;
  let buttonText = 'Explorar Propiedades';
  let buttonPath = '/buscar';
  
  switch(activeType) {
    case 'propiedad':
      message = 'No has guardado propiedades en venta aún.';
      icon = Home;
      buttonText = 'Explorar Propiedades';
      buttonPath = '/buscar?tipo=propiedad';
      break;
    case 'alquiler':
      message = 'No has guardado propiedades en alquiler aún.';
      icon = Building;
      buttonText = 'Explorar Alquileres';
      buttonPath = '/buscar?tipo=alquiler';
      break;
    case 'proyecto':
      message = 'No has guardado proyectos inmobiliarios aún.';
      icon = Castle;
      buttonText = 'Explorar Proyectos';
      buttonPath = '/proyectos';
      break;
    default:
      message = 'No tienes favoritos aún. ¡Empieza a explorar!';
      buttonText = 'Explorar Propiedades';
      buttonPath = '/buscar';
  }

  const IconComponent = icon;
  
  return (
    <motion.div 
      className="text-center py-16"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <motion.div 
        className="bg-indigo-50 dark:bg-indigo-900/30 w-24 h-24 rounded-full mx-auto mb-6 flex items-center justify-center"
        whileHover={{ rotate: 12, scale: 1.05 }}
        transition={{ type: "spring", stiffness: 300, damping: 10 }}
      >
        <IconComponent className="w-12 h-12 text-indigo-400 dark:text-indigo-300" />
      </motion.div>
      <h3 className="text-2xl font-bold text-gray-800 dark:text-gray-200 mb-3">
        No tienes favoritos
      </h3>
      <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto mb-8 leading-relaxed">
        {message}
      </p>
      <motion.button
        onClick={() => window.location.href = buttonPath}
        className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-indigo-600 to-blue-600 text-white font-medium rounded-xl shadow-lg hover:shadow-xl dark:shadow-xl dark:hover:shadow-2xl"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <Home className="w-5 h-5" />
        <span>{buttonText}</span>
      </motion.button>
    </motion.div>
  );
};

const FilterControls = ({ onFilterApply, onClearFilters, hasActiveFilters }) => {
  const [filters, setFilters] = useState({
    bedrooms: '',
    bathrooms: '',
    minArea: '',
    maxArea: ''
  });
  const [isOpen, setIsOpen] = useState(false);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleApplyFilters = () => {
    onFilterApply(filters);
    setIsOpen(false);
  };

  const handleClearFilters = () => {
    setFilters({
      bedrooms: '',
      bathrooms: '',
      minArea: '',
      maxArea: ''
    });
    onClearFilters();
    setIsOpen(false);
  };

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-4">
        <motion.button
          onClick={() => setIsOpen(!isOpen)}
          className={`flex items-center gap-2 px-5 py-3 rounded-xl transition-all duration-300 
                     backdrop-blur-xl border shadow-lg dark:shadow-black/30 ${
                       hasActiveFilters 
                       ? 'bg-indigo-500/20 text-indigo-700 dark:text-indigo-300 border-indigo-300/30 dark:border-indigo-500/30' 
                       : 'bg-white/10 dark:bg-white/5 hover:bg-white/20 dark:hover:bg-white/10 text-gray-700 dark:text-gray-200 border-white/20 dark:border-white/10'
                     }`}
          whileHover={{ scale: 1.02, y: -2 }}
          whileTap={{ scale: 0.98 }}
        >
          <Filter className={`w-5 h-5 ${hasActiveFilters ? 'text-indigo-500 dark:text-indigo-400' : 'text-gray-500 dark:text-gray-400'}`} />
          <span className="font-medium">Filtros</span>
          {hasActiveFilters && (
            <div className="bg-indigo-500 text-white w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ml-1 shadow-lg">
              !
            </div>
          )}
        </motion.button>

        <motion.select
          onChange={(e) => {
            if (e.target.value === 'none') return;
            onFilterApply({ ...filters, sort: e.target.value });
          }}
          className="px-5 py-3 rounded-xl border border-white/20 dark:border-white/10 backdrop-blur-xl bg-white/10 dark:bg-white/5 text-gray-700 dark:text-gray-200 focus:border-indigo-300/50 dark:focus:border-indigo-500/50 focus:ring focus:ring-indigo-200/50 dark:focus:ring-indigo-800/50 focus:ring-opacity-50 font-medium shadow-lg dark:shadow-black/30 hover:bg-white/20 dark:hover:bg-white/10 transition-all duration-200
                   [&>option]:bg-white [&>option]:dark:bg-gray-800 [&>option]:text-gray-900 [&>option]:dark:text-gray-100 [&>option]:py-2 [&>option]:px-3"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
            backgroundPosition: 'right 0.5rem center',
            backgroundRepeat: 'no-repeat',
            backgroundSize: '1.5em 1.5em',
            paddingRight: '2.5rem'
          }}
          whileHover={{ scale: 1.02 }}
        >
          <option value="none" className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">Ordenar por</option>
          <option value="asc" className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">Menor a Mayor precio</option>
          <option value="desc" className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">Mayor a Menor precio</option>
          <option value="newest" className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">Más recientes</option>
        </motion.select>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="relative backdrop-blur-xl bg-white/10 dark:bg-white/5 rounded-2xl shadow-xl dark:shadow-black/50 p-6 border border-white/20 dark:border-white/10 overflow-hidden">
              {/* Gradient overlay */}
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-indigo-500/5 to-purple-500/5 dark:from-indigo-400/10 dark:to-purple-400/10" />
              
              <div className="relative z-10">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Habitaciones
                    </label>
                    <div className="relative">
                      <select
                        name="bedrooms"
                        value={filters.bedrooms}
                        onChange={handleFilterChange}
                        className="w-full appearance-none px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 backdrop-blur-sm bg-white/20 dark:bg-white/10 text-gray-700 dark:text-gray-200
                                focus:border-indigo-300/50 dark:focus:border-indigo-500/50 focus:ring focus:ring-indigo-200/50 dark:focus:ring-indigo-800/50 focus:ring-opacity-50 pr-10 hover:bg-white/30 dark:hover:bg-white/20 transition-all duration-200"
                      >
                        <option value="">Todos</option>
                        <option value="1">1+</option>
                        <option value="2">2+</option>
                        <option value="3">3+</option>
                        <option value="4">4+</option>
                      </select>
                      <Bed className="absolute right-3 top-1/2 transform -translate-y-1/2 text-indigo-400 dark:text-indigo-300 w-5 h-5" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Baños
                    </label>
                    <div className="relative">
                      <select
                        name="bathrooms"
                        value={filters.bathrooms}
                        onChange={handleFilterChange}
                        className="w-full appearance-none px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 backdrop-blur-sm bg-white/20 dark:bg-white/10 text-gray-700 dark:text-gray-200
                                focus:border-indigo-300/50 dark:focus:border-indigo-500/50 focus:ring focus:ring-indigo-200/50 dark:focus:ring-indigo-800/50 focus:ring-opacity-50 pr-10 hover:bg-white/30 dark:hover:bg-white/20 transition-all duration-200"
                      >
                        <option value="">Todos</option>
                        <option value="1">1+</option>
                        <option value="2">2+</option>
                        <option value="3">3+</option>
                      </select>
                      <Bath className="absolute right-3 top-1/2 transform -translate-y-1/2 text-indigo-400 dark:text-indigo-300 w-5 h-5" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Área mínima (m²)
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        name="minArea"
                        value={filters.minArea}
                        onChange={handleFilterChange}
                        placeholder="Mín"
                        min="0"
                        className="w-full px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 backdrop-blur-sm bg-white/20 dark:bg-white/10 text-gray-700 dark:text-gray-200
                                focus:border-indigo-300/50 dark:focus:border-indigo-500/50 focus:ring focus:ring-indigo-200/50 dark:focus:ring-indigo-800/50 focus:ring-opacity-50 pr-10 hover:bg-white/30 dark:hover:bg-white/20 transition-all duration-200"
                      />
                      <Square className="absolute right-3 top-1/2 transform -translate-y-1/2 text-indigo-400 dark:text-indigo-300 w-5 h-5" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Área máxima (m²)
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        name="maxArea"
                        value={filters.maxArea}
                        onChange={handleFilterChange}
                        placeholder="Máx"
                        min="0"
                        className="w-full px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 backdrop-blur-sm bg-white/20 dark:bg-white/10 text-gray-700 dark:text-gray-200
                                focus:border-indigo-300/50 dark:focus:border-indigo-500/50 focus:ring focus:ring-indigo-200/50 dark:focus:ring-indigo-800/50 focus:ring-opacity-50 pr-10 hover:bg-white/30 dark:hover:bg-white/20 transition-all duration-200"
                      />
                      <Square className="absolute right-3 top-1/2 transform -translate-y-1/2 text-indigo-400 dark:text-indigo-300 w-5 h-5" />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-6">
                  <motion.button
                    onClick={handleClearFilters}
                    className="px-4 py-2 rounded-xl border border-white/20 dark:border-white/10 backdrop-blur-sm
                             text-gray-600 dark:text-gray-300 bg-white/20 dark:bg-white/10 hover:bg-white/30 dark:hover:bg-white/20 transition-all duration-200 shadow-lg"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <div className="flex items-center gap-1">
                      <X className="w-4 h-4" />
                      Limpiar
                    </div>
                  </motion.button>

                  <motion.button
                    onClick={handleApplyFilters}
                    className="px-6 py-2 bg-gradient-to-r from-indigo-600 to-blue-600 text-white rounded-xl shadow-xl hover:shadow-2xl backdrop-blur-sm"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      Aplicar
                    </div>
                  </motion.button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Componente de estadísticas de favoritos con glassmorphism
const FavoritesStats = ({ counts }) => {
  const stats = [
    {
      key: 'propiedad',
      label: 'Propiedades',
      icon: Home,
      color: 'purple',
      count: counts.propiedad || 0
    },
    {
      key: 'alquiler',
      label: 'Alquileres',
      icon: Building,
      color: 'blue',
      count: counts.alquiler || 0
    },
    {
      key: 'proyecto',
      label: 'Proyectos',
      icon: Castle,
      color: 'orange',
      count: counts.proyecto || 0
    }
  ];

  const colorSchemes = {
    purple: { gradient: "from-purple-500 to-purple-600", glow: "bg-purple-500/20" },
    blue: { gradient: "from-blue-500 to-blue-600", glow: "bg-blue-500/20" },
    orange: { gradient: "from-orange-500 to-orange-600", glow: "bg-orange-500/20" }
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
      {stats.map((stat, index) => {
        const colors = colorSchemes[stat.color];
        
        return (
          <motion.div
            key={stat.key}
            className="group relative overflow-hidden"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1, duration: 0.6 }}
            whileHover={{ y: -4, scale: 1.02 }}
          >
            {/* Glassmorphism container */}
            <div className="relative backdrop-blur-xl bg-white/10 dark:bg-white/5 rounded-2xl p-4 border border-white/20 dark:border-white/10 shadow-lg dark:shadow-black/30 transition-all duration-300 group-hover:bg-white/20 dark:group-hover:bg-white/10 group-hover:shadow-xl dark:group-hover:shadow-black/50">
              
              {/* Gradient overlay */}
              <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${colors.gradient} opacity-5 group-hover:opacity-10 transition-opacity duration-300`} />
              
              <div className="relative z-10 flex items-center gap-4">
                {/* Icon container */}
                <div className="relative">
                  {/* Glow effect */}
                  <div className={`absolute inset-0 ${colors.glow} rounded-2xl blur-lg opacity-0 group-hover:opacity-70 transition-opacity duration-300`} />
                  
                  <div className={`relative p-3 rounded-2xl bg-gradient-to-br ${colors.gradient} backdrop-blur-sm shadow-lg transition-transform duration-300 group-hover:scale-110`}>
                    <stat.icon className="w-6 h-6 text-white" />
                  </div>
                </div>
                
                {/* Content */}
                <div>
                  <p className="text-gray-600 dark:text-gray-300 text-sm opacity-90">{stat.label}</p>
                  <p className={`text-2xl font-bold transition-colors duration-300 ${
                    stat.color === 'purple' ? 'text-purple-700 dark:text-purple-300' :
                    stat.color === 'blue' ? 'text-blue-700 dark:text-blue-300' :
                    'text-orange-700 dark:text-orange-300'
                  }`}>
                    {stat.count}
                  </p>
                </div>
              </div>
              
              {/* Bottom accent line */}
              <div className={`absolute bottom-0 left-0 h-0.5 bg-gradient-to-r ${colors.gradient} rounded-full transition-all duration-500 group-hover:w-full w-0 opacity-80`} />
            </div>
          </motion.div>
        );
      })}
    </div>
  );
};

const Favorites = () => {
  const navigate = useNavigate();
  const { fetchWithAuth } = useAuth();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [favorites, setFavorites] = useState([]);
  const [filteredFavorites, setFilteredFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasActiveFilters, setHasActiveFilters] = useState(false);
  const [activeType, setActiveType] = useState('all'); // Para el filtrado por tipo
  const [counts, setCounts] = useState({ total: 0, propiedad: 0, alquiler: 0, proyecto: 0 });
  const { toast } = useToast();
  const itemsPerPage = 9;

  // Verificar autenticación al cargar el componente
  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('token');
      const user = localStorage.getItem('user');
      
      if (token && user) {
        try {
          const parsedUser = JSON.parse(user);
          // Verificar que el usuario tenga tipo_usuario_id
          if (parsedUser.tipo_usuario_id) {
            setIsAuthenticated(true);
          } else {
            // Si no tiene tipo_usuario_id, limpiar y cerrar sesión
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            setIsAuthenticated(false);
          }
        } catch (error) {
          console.error('Error parsing user data:', error);
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setIsAuthenticated(false);
        }
      } else {
        setIsAuthenticated(false);
      }
      
      setCheckingAuth(false);
    };

    checkAuth();
  }, []);

  // Cargar favoritos cuando el usuario está autenticado
  useEffect(() => {
    const fetchFavorites = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetchWithAuth('https://backend-production-1786.up.railway.app/api/favoritos');
        
        // Si response es null (sesión expirada), salir de la función
        if (!response) {
          setIsAuthenticated(false);
          return;
        }

        // Obtener los datos de la API
        const data = await response.json();
        
        // Aplicar solo cambios mínimos sin alterar el formato de los datos originales
        // De forma similar a como se hace en Home.jsx
        const formattedFavorites = data.map(property => ({
          ...property,
          tipo_elemento: property.tipo_elemento || 
                       (property.operacion === 'alquiler' ? 'alquiler' : 'propiedad'),
          isFavorite: true,
          operacion: property.operacion === 'alquiler' ? 'alquiler' : 
                   property.tipo_elemento === 'proyecto' ? 'proyecto' : 'comprar'
        }));

        setFavorites(formattedFavorites);
        setFilteredFavorites(formattedFavorites);
        
        // Calcular conteos para las estadísticas
        const typeCounts = {
          total: formattedFavorites.length,
          propiedad: formattedFavorites.filter(item => item.tipo_elemento === 'propiedad').length,
          alquiler: formattedFavorites.filter(item => item.tipo_elemento === 'alquiler').length,
          proyecto: formattedFavorites.filter(item => item.tipo_elemento === 'proyecto').length
        };
        
        setCounts(typeCounts);
      } catch (error) {
        console.error('Error fetching favorites:', error);
        
        // Solo mostrar error si no es por sesión expirada
        if (error.message !== 'Sesión expirada') {
          setError(error.message);
          toast({
            title: "Error al cargar favoritos",
            description: error.message,
            variant: "destructive",
          });
        } else {
          // Si la sesión expiró, actualizar el estado de autenticación
          setIsAuthenticated(false);
        }
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated) {
      fetchFavorites();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, toast, fetchWithAuth]);

  // Manejador para el cambio de tipo (propiedad, alquiler, proyecto)
  const handleTypeChange = (type) => {
    setActiveType(type);
    
    if (type === 'all') {
      setFilteredFavorites(favorites);
    } else {
      const filtered = favorites.filter(item => item.tipo_elemento === type);
      setFilteredFavorites(filtered);
    }
    
    setCurrentPage(1);
    setHasActiveFilters(false); // Resetear los filtros activos
  };

  const handleFilterApply = (filters) => {
    // Primero filtramos por tipo si está activo
    let filtered = activeType === 'all' 
      ? [...favorites] 
      : favorites.filter(item => item.tipo_elemento === activeType);

    if (filters.bedrooms) {
      filtered = filtered.filter(property => {
        // Para propiedades con rangos como "1-2"
        if (typeof property.habitaciones === 'string' && property.habitaciones.includes('-')) {
          const min = parseInt(property.habitaciones.split('-')[0]);
          return !isNaN(min) && min >= parseInt(filters.bedrooms);
        }
        return !isNaN(parseInt(property.habitaciones)) && parseInt(property.habitaciones) >= parseInt(filters.bedrooms);
      });
    }

    if (filters.bathrooms) {
      filtered = filtered.filter(property => 
        !isNaN(parseInt(property.banos)) && parseInt(property.banos) >= parseInt(filters.bathrooms)
      );
    }

    if (filters.minArea) {
      filtered = filtered.filter(property => 
        !isNaN(parseInt(property.area_total)) && parseInt(property.area_total) >= parseInt(filters.minArea)
      );
    }

    if (filters.maxArea) {
      filtered = filtered.filter(property => 
        !isNaN(parseInt(property.area_total)) && parseInt(property.area_total) <= parseInt(filters.maxArea)
      );
    }

    if (filters.sort === 'asc') {
      filtered.sort((a, b) => {
        const priceA = a.tipo_elemento === 'alquiler' ? a.precio_mensual : 
                      a.tipo_elemento === 'proyecto' ? a.precio_desde : a.precio_soles;
        const priceB = b.tipo_elemento === 'alquiler' ? b.precio_mensual : 
                      b.tipo_elemento === 'proyecto' ? b.precio_desde : b.precio_soles;
        return priceA - priceB;
      });
    } else if (filters.sort === 'desc') {
      filtered.sort((a, b) => {
        const priceA = a.tipo_elemento === 'alquiler' ? a.precio_mensual : 
                      a.tipo_elemento === 'proyecto' ? a.precio_desde : a.precio_soles;
        const priceB = b.tipo_elemento === 'alquiler' ? b.precio_mensual : 
                      b.tipo_elemento === 'proyecto' ? b.precio_desde : b.precio_soles;
        return priceB - priceA;
      });
    } else if (filters.sort === 'newest') {
      filtered.sort((a, b) => {
        const dateA = new Date(a.fecha_creacion || a.fecha_publicacion);
        const dateB = new Date(b.fecha_creacion || b.fecha_publicacion);
        return dateB - dateA;
      });
    }

    setFilteredFavorites(filtered);
    setCurrentPage(1); // Resetear a la primera página cuando se aplican filtros
    
    // Check if any filters are active
    setHasActiveFilters(
      filters.bedrooms || filters.bathrooms || filters.minArea || filters.maxArea || filters.sort
    );
  };

  const handleClearFilters = () => {
    if (activeType === 'all') {
      setFilteredFavorites(favorites);
    } else {
      const filtered = favorites.filter(item => item.tipo_elemento === activeType);
      setFilteredFavorites(filtered);
    }
    setCurrentPage(1);
    setHasActiveFilters(false);
  };

  const handleFavoriteUpdate = (propertyId, isFavorite) => {
    if (!isFavorite) {
      // Actualizar conteos
      const itemToRemove = favorites.find(item => item.id === propertyId);
      if (itemToRemove) {
        setCounts(prev => ({
          ...prev,
          total: prev.total - 1,
          [itemToRemove.tipo_elemento]: prev[itemToRemove.tipo_elemento] - 1
        }));
      }

      // Eliminar de la lista
      setFavorites((prevFavorites) =>
        prevFavorites.filter((property) => property.id !== propertyId)
      );
      setFilteredFavorites((prevFiltered) =>
        prevFiltered.filter((property) => property.id !== propertyId)
      );

      // Notificar al usuario
      toast({
        title: "Eliminado de favoritos",
        description: "El elemento se eliminó de tus favoritos",
        variant: "default",
      });
    }
  };

  if (checkingAuth || loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <motion.div 
          className="max-w-md bg-white dark:bg-gray-800 rounded-2xl shadow-xl dark:shadow-2xl p-8 text-center border border-gray-100 dark:border-gray-700"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="bg-red-50 dark:bg-red-900/30 w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center">
            <X className="w-8 h-8 text-red-500 dark:text-red-400" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200 mb-4">Ha ocurrido un error</h2>
          <p className="text-red-500 dark:text-red-400 mb-6">{error}</p>
          <motion.button
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Reintentar
          </motion.button>
        </motion.div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginPrompt onLogin={() => navigate('/login')} />;
  }

  // Cálculos para la paginación
  const totalPages = Math.ceil(filteredFavorites.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentFavorites = filteredFavorites.slice(startIndex, endIndex);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-12 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Page Header */}
        <motion.div 
          className="mb-10"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div className="flex items-center gap-4">
              <div className="bg-indigo-100 dark:bg-indigo-900/30 p-4 rounded-xl">
                <Heart className="w-8 h-8 text-indigo-500 dark:text-indigo-400" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100 mb-1">Mis Favoritos</h1>
                <p className="text-gray-500 dark:text-gray-400">
                  {counts.total} {counts.total === 1 ? 'elemento' : 'elementos'} guardados
                </p>
              </div>
            </div>
            
            <motion.button
              onClick={() => navigate('/buscar')}
              className="flex items-center gap-2 px-6 py-3 bg-white dark:bg-gray-800
                        rounded-xl text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-600
                        hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-300 shadow-sm dark:shadow-lg"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Search className="w-5 h-5" />
              <span>Explorar Propiedades</span>
            </motion.button>
          </div>

          {/* Estadísticas */}
          <FavoritesStats counts={counts} />
        </motion.div>

        {/* Main Content */}
        <motion.div 
          className="relative backdrop-blur-xl bg-white/10 dark:bg-white/5 rounded-2xl shadow-xl dark:shadow-black/50 overflow-hidden border border-white/20 dark:border-white/10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {/* Gradient overlay */}
          <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-blue-500/5 to-purple-500/5 dark:from-blue-400/10 dark:to-purple-400/10" />
          
          <div className="relative z-10 p-8">
            {/* Tabs para filtrar por tipo */}
            <TypeTabs 
              activeType={activeType} 
              onChange={handleTypeChange}
              counts={counts}
            />
            
            {/* Filters Section */}
            <FilterControls 
              onFilterApply={handleFilterApply} 
              onClearFilters={handleClearFilters}
              hasActiveFilters={hasActiveFilters}
            />

            {/* Results */}
            {filteredFavorites.length === 0 ? (
              <EmptyState activeType={activeType} />
            ) : (
              <>
                <motion.div 
                  className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
                  variants={staggerContainer}
                  initial="hidden"
                  animate="visible"
                >
                  {currentFavorites.map((item) => (
                  <motion.div
                    key={`${item.id}-${item.tipo_elemento}`}
                    variants={cardAnimation}
                    whileHover="hover"
                    className="h-full"
                  >
                    {item.tipo_elemento === 'proyecto' ? (
                      <PropertyCardPro
                        item={item}
                        isProject={true}
                        onFavoriteUpdate={handleFavoriteUpdate}
                      />
                    ) : (
                      <PropertyCard
                        property={item}
                        onFavoriteUpdate={handleFavoriteUpdate}
                      />
                    )}
                  </motion.div>
                ))}
                </motion.div>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <motion.div 
                    className="flex justify-between items-center mt-10"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.4, duration: 0.6 }}
                  >
                    <PageButton
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                      direction="prev"
                    >
                      Anterior
                    </PageButton>

                    <div className="flex items-center gap-2">
                      {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                        let pageNumber;
                        // Lógica para mostrar los números de página
                        if (totalPages <= 5) {
                          pageNumber = i + 1;
                        } else if (currentPage <= 3) {
                          pageNumber = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNumber = totalPages - 4 + i;
                        } else {
                          pageNumber = currentPage - 2 + i;
                        }
                        
                        return (
                          <button
                            key={pageNumber}
                            onClick={() => setCurrentPage(pageNumber)}
                            className={`w-10 h-10 flex items-center justify-center rounded-full transition-all duration-200
                                     ${currentPage === pageNumber 
                                       ? 'bg-indigo-600 text-white shadow-lg' 
                                       : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'}`}
                          >
                            {pageNumber}
                          </button>
                        );
                      })}
                    </div>

                    <PageButton
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      direction="next"
                    >
                      Siguiente
                    </PageButton>
                  </motion.div>
                )}
              </>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Favorites;